import React, {useContext, useEffect} from "react";
import {Box, Paper, Stack, Typography} from "@mui/material";
import {Controller, FormProvider, useForm} from "react-hook-form";


import {AuthenticationContext} from "../../AuthenticateView";
import {LoginCredentials} from "../../types";
import {AuthenticationHandler, useAuthenticate} from "../../useAuthenticate";
import ErrorBanner from "../../../../common/ErrorBanner";
import CancelMfaSignInButton from "../../../../common/CancelMfaSignInButton";
import MfaTotpImage from "../../../../common/images/MfaTotpImage";
import {useLxTranslations} from "@lxo-blocks/react";
import {TOTP_FORM_TRANSLATION} from "./TotpForm.translation.de";
import {MfaTotpRemeberDeviceFormCheckbox} from "./components/MfaTotpRemeberDeviceFormCheckbox";
import {TotpCodeFormInput} from "./components/TotpCodeFormInput";
import ChangeMethodButton from "../changeMethod/components/ChangeMethodButton";


interface MfaFormProps {
    authenticationHandler: AuthenticationHandler,
}

export default function TotpForm(props: MfaFormProps) {
    const authenticationContext = useContext(AuthenticationContext);
    const formMethods = useForm<LoginCredentials>({
        mode: "onSubmit",
        defaultValues: {
            totptoken: "",
            rememberDevice: false
        }
    });
    const {t} = useLxTranslations(TOTP_FORM_TRANSLATION);
    const {authenticate, authenticationError} = useAuthenticate(props.authenticationHandler);
    const {handleSubmit, control, watch, setError} = formMethods;
    const totp = watch("totptoken");


    const isValidTotpCode = (totpCode: string | undefined) => totpCode && totpCode.length === 7;

    function getNormalizedTotpCode(inputCode: string | undefined): string {
        return inputCode ? inputCode.replace(/[^0-9]/gi, "").trim() : "";
    }

    // ################################################################################################
    //                     auto confirm
    // ################################################################################################
    useEffect(() => {
        if (isValidTotpCode(totp)) {
            handleSubmit(enrichCodeAndSubmit)()
        } // eslint-disable-next-line
    }, [totp]);
    // ################################################################################################

    useEffect(() => {
        if (authenticationError) {
            setError("totptoken", authenticationError)
        } // eslint-disable-next-line
    }, [authenticationError]);

    const enrichCodeAndSubmit = async (loginCredentials: LoginCredentials) => {
        loginCredentials.mfaMethod = "TOTP";
        loginCredentials.totptoken = getNormalizedTotpCode(loginCredentials.totptoken)
        return authenticate({...authenticationContext, ...loginCredentials});
    }

    return (
        <>
            <Paper sx={{pr: 2, pl: 2, pt: 2, pb: 7}}>
                {authenticationError && (
                    <ErrorBanner headline={t("LOGIN_TOTP_FAILED")}
                                 description={authenticationError.description ? authenticationError.description : t("LOGIN_TOTP_FAILED_TRY_AGAIN")}
                                 sx={{mb: 3}}/>)}

                <Box display="flex"
                     justifyContent="center"
                     alignItems="center"> <Typography className="Title" variant="body2"
                                                      sx={{pb: 2}}>{t("TOTP_TEXT")}</Typography></Box>

                <Box sx={{p: 3}}
                     display="flex"
                     justifyContent="center"
                     alignItems="center">
                    <MfaTotpImage/>
                </Box>

                <FormProvider {...formMethods}>
                    <form onSubmit={handleSubmit(enrichCodeAndSubmit)}>

                        <Box sx={{pt: 2, pb: 2, pr: 8, pl: 8}}
                             display="flex"
                             justifyContent="center"
                             alignItems="center">
                            <Controller
                                control={control}
                                name="rememberDevice"
                                render={(props: any) => <MfaTotpRemeberDeviceFormCheckbox {...props}
                                                                                          sx={{mb: 3, ml: 2}}/>}/>
                        </Box>

                        <Box sx={{pr: 20, pl: 20}}
                             display="flex"
                             justifyContent="center"
                             alignItems="center">
                            <Controller
                                name="totptoken"
                                control={control}
                                defaultValue=""
                                rules={{
                                    required: t("TOTP_CODE_REQUIRED").toString(),
                                    minLength: 7,
                                    maxLength: 7
                                }}
                                render={(props: any) => <TotpCodeFormInput {...props} autoFocus={true}/>}
                            />
                        </Box>
                    </form>
                </FormProvider>
            </Paper>

            <Stack spacing={2} direction="row-reverse" style={{float: "right"}} mt={2}>
                <ChangeMethodButton onChangeMethod={props.authenticationHandler.onChangeMethod}/>
                <CancelMfaSignInButton/>
            </Stack>

        </>
    );
}
